import { useContext } from 'react';
import { AuthContext } from '../../providers/AuthProvider';
import { ApiMethod } from '../models/api-types.type';
import { routes } from '../../assets/constants';
import AuthClientStore from '../services/auth-client.service';
import { getDeviceId } from '../services/device-session.service';
import { useAuthApi } from './use-auth-api.hook';
import type {
  InboxCounts,
  StaffConversation,
  StaffConversationDetail,
  SupportAttachment,
  SupportContext,
  SupportMessage,
  SupportTask,
  TaskStatus,
  VendorConversation,
  StaffMember,
} from '../models/support.model';

const API_URL = process.env.REACT_APP_PMS_API_URL as string;

/**
 * Multipart upload with progress (fetch can't report upload progress, and a
 * 40 MB screen recording needs a progress bar). Retries once after a token
 * refresh, like sendAuthGuardedRequest.
 */
export function uploadWithProgress(
  path: string,
  file: File,
  onProgress?: (fraction: number) => void,
  refresh?: () => Promise<unknown>,
): Promise<SupportAttachment> {
  const attempt = (): Promise<{ status: number; body: any }> =>
    new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', API_URL + path);
      const token = AuthClientStore.getAccessToken();
      if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.setRequestHeader('X-Device-Id', getDeviceId());
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress?.(e.loaded / e.total);
      };
      xhr.onload = () => {
        let body: any = null;
        try {
          body = JSON.parse(xhr.responseText);
        } catch {
          // keep null
        }
        resolve({ status: xhr.status, body });
      };
      xhr.onerror = () => reject(new Error('Upload failed — check your connection.'));
      const form = new FormData();
      form.append('file', file, file.name);
      xhr.send(form);
    });

  return attempt().then(async (res) => {
    let { status, body } = res;
    if (status === 401 && refresh) {
      await refresh();
      ({ status, body } = await attempt());
    }
    if (status >= 400) {
      const message = Array.isArray(body?.message) ? body.message.join(', ') : body?.message;
      throw new Error(message || (status === 413 ? 'This file is too large.' : `Upload failed (${status}).`));
    }
    return body as SupportAttachment;
  });
}

export interface NewConversationPayload {
  subject?: string;
  body?: string;
  category?: string;
  attachments?: SupportAttachment[];
  context?: SupportContext;
}

export interface MessagePayload {
  body?: string;
  attachments?: SupportAttachment[];
  internal?: boolean;
}

/** The vendor's side of support: conversations with Ardealy. */
export const useSupportApi = () => {
  const { sendAuthGuardedRequest } = useContext(AuthContext);
  const { refreshTokens } = useAuthApi();
  const r = routes.support;

  return {
    listConversations: () => sendAuthGuardedRequest(ApiMethod.GET, r.conversations) as Promise<VendorConversation[]>,
    getUnread: () => sendAuthGuardedRequest(ApiMethod.GET, r.unread) as Promise<{ count: number }>,
    getConversation: (id: string) =>
      sendAuthGuardedRequest(ApiMethod.GET, r.conversation(id)) as Promise<{ conversation: VendorConversation; messages: SupportMessage[] }>,
    createConversation: (payload: NewConversationPayload) =>
      sendAuthGuardedRequest(ApiMethod.POST, r.conversations, payload) as Promise<{ conversation: VendorConversation; message: SupportMessage }>,
    sendMessage: (id: string, payload: MessagePayload) =>
      sendAuthGuardedRequest(ApiMethod.POST, r.messages(id), payload) as Promise<SupportMessage>,
    markRead: (id: string) => sendAuthGuardedRequest(ApiMethod.POST, r.read(id)),
    upload: (file: File, onProgress?: (f: number) => void) => uploadWithProgress(r.attachments, file, onProgress, refreshTokens),
  };
};

export interface TaskPayload {
  type?: string;
  title?: string;
  description?: string;
  status?: string;
  priority?: string;
  vendorId?: string;
  conversationId?: string;
  assignee?: string;
  labels?: string[];
  visibleToVendor?: boolean;
}

const qs = (params: Record<string, string | undefined>) => {
  const s = new URLSearchParams(Object.entries(params).filter(([, v]) => v) as [string, string][]).toString();
  return s ? `?${s}` : '';
};

/** Ardealy staff: the inbox across vendors and the task board. */
export const useSupportAdminApi = () => {
  const { sendAuthGuardedRequest } = useContext(AuthContext);
  const { refreshTokens } = useAuthApi();
  const r = routes.supportAdmin;

  return {
    listConversations: (q: { status?: string; q?: string; vendorId?: string; assignee?: string } = {}) =>
      sendAuthGuardedRequest(ApiMethod.GET, r.conversations + qs(q)) as Promise<{ items: StaffConversation[]; counts: InboxCounts }>,
    getSummary: () => sendAuthGuardedRequest(ApiMethod.GET, r.summary) as Promise<InboxCounts>,
    listStaff: () => sendAuthGuardedRequest(ApiMethod.GET, r.staff) as Promise<StaffMember[]>,
    getConversation: (id: string) => sendAuthGuardedRequest(ApiMethod.GET, r.conversation(id)) as Promise<StaffConversationDetail>,
    reply: (id: string, payload: MessagePayload) => sendAuthGuardedRequest(ApiMethod.POST, r.messages(id), payload) as Promise<SupportMessage>,
    updateConversation: (id: string, patch: Partial<Pick<StaffConversation, 'status' | 'priority' | 'category' | 'assignee' | 'subject'>>) =>
      sendAuthGuardedRequest(ApiMethod.PATCH, r.conversation(id), patch) as Promise<StaffConversation>,
    markRead: (id: string) => sendAuthGuardedRequest(ApiMethod.POST, r.read(id)),
    upload: (file: File, onProgress?: (f: number) => void) => uploadWithProgress(r.attachments, file, onProgress, refreshTokens),

    listTasks: (q: { type?: string; q?: string; vendorId?: string; assignee?: string } = {}) =>
      sendAuthGuardedRequest(ApiMethod.GET, r.tasks + qs(q)) as Promise<SupportTask[]>,
    getTask: (id: string) => sendAuthGuardedRequest(ApiMethod.GET, r.task(id)) as Promise<SupportTask>,
    createTask: (payload: TaskPayload) => sendAuthGuardedRequest(ApiMethod.POST, r.tasks, payload) as Promise<SupportTask>,
    updateTask: (id: string, patch: TaskPayload) => sendAuthGuardedRequest(ApiMethod.PATCH, r.task(id), patch) as Promise<SupportTask>,
    moveTask: (id: string, move: { status: TaskStatus; beforeId?: string; afterId?: string }) =>
      sendAuthGuardedRequest(ApiMethod.POST, r.move(id), move) as Promise<SupportTask>,
    linkTask: (id: string, conversationId: string) =>
      sendAuthGuardedRequest(ApiMethod.POST, r.link(id), { conversationId }) as Promise<SupportTask>,
    deleteTask: (id: string) => sendAuthGuardedRequest(ApiMethod.DELETE, r.task(id)),
  };
};
